/**
 * DE/EN string table, ported verbatim from docs/handover/reference/i18n.js.
 *
 * DE is the source language -- the original spreadsheet and the client are
 * German. Do not reword the German; it is the designer's copy, written to a
 * house voice ("errors name the fix, not the failure").
 */

export type Lang = "de" | "en";

export interface Strings {
  lang: string;
  appName: string;
  steps: [string, string, string];
  disciplineQ: string;
  bike: string;
  run: string;
  swim: string;
  sportBike: string;
  sportRun: string;
  sportTri: string;
  sportDu: string;
  nextTo: string;
  stageBike: string;
  stageRun: string;
  stageSwim: string;
  swimHint: string;
  swimPre: string;
  preShort: string;
  save: string;
  saveCta: string;
  saved: string;
  savedPlans: string;
  planName: string;
  planNameHint: string;
  raceDate: string;
  newPlan: string;
  load: string;
  del: string;
  noPlans: string;
  updated: string;
  savedToast: string;
  updatedToast: string;
  deletedToast: string;
  saveNote: string;
  myPlans: string;
  unsaved: string;
  totals: string;
  totalCarbs: string;
  totalTime: string;
  avgPerHour: string;
  perLeg: string;
  weight: string;
  weightField: string;
  duration: string;
  from: string;
  to: string;
  minutes: string;
  bottle: string;
  bottleField: string;
  cup: string;
  cupField: string;
  stations: string;
  stationsShort: string;
  next: string;
  back: string;
  calcCta: string;
  editInputs: string;
  drinks: string;
  gels: string;
  bars: string;
  none: string;
  fluid: string;
  count: string;
  addLine: string;
  resultTitle: string;
  perHour: string;
  ceiling: string;
  ceilingShort: string;
  ofCeiling: string;
  onTarget: string;
  over: string;
  under: string;
  total: string;
  kcal: string;
  bottles: string;
  cups: string;
  perStation: string;
  items: string;
  eatEvery: string;
  drinkEvery: string;
  schedule: string;
  sips: string;
  gel: string;
  bar: string;
  drink: string;
  disclaimer: string;
  emptyProducts: string;
  liveNow: string;
  liveOf: string;
  liveCeiling: string;
  liveNothing: string;
  swimNoLimit: string;
  carbsSelected: string;
  swimDisclaimer: string;
  rangeNote: string;
  /** Added here, not in the handoff: FR-1.7 requires a validation message and
   *  the README dictates its wording. */
  durationOrder: string;
  /** Last-leg CTA. Needs its own string: "Verpflegung" is feminine, so the
   *  generic `nextTo` ("Weiter zum") produces the wrong article. */
  nextToFuel: string;
  /** Step meta in the header, per README ("ship as 'Schritt 1 von 3'"). */
  stepMeta: string;
  legsEyebrow: string;
  legsOne: string;
  legsMany: string;
  /** Add-to-home-screen banner. Not from the design handoff -- see
   *  features/install/InstallPrompt.vue. */
  installTitle: string;
  installBody: string;
  installCta: string;
  installIos: string;
  installDismiss: string;
}

export const I18N: Record<Lang, Strings> = {
  de: {
    lang: "DE",
    appName: "carbdial",
    steps: ["Rennen", "Verpflegung", "Plan"],
    disciplineQ: "Welche Sportart?",
    bike: "Rad",
    run: "Laufen",
    swim: "Schwimmen",
    sportBike: "Radrennen",
    sportRun: "Laufen (Marathon, Trail …)",
    sportTri: "Triathlon (Schwimmen · Rad · Laufen)",
    sportDu: "Duathlon (Laufen · Rad · Laufen)",
    nextTo: "Weiter zum",
    stageBike: "Radteil",
    stageRun: "Laufteil",
    stageSwim: "Schwimmteil",
    swimHint:
      "Beim Schwimmen wird nicht getrunken. Üblich ist ein Gel ca. 1 Minute vor dem Start — wir rechnen es mit, damit die Gesamtmenge stimmt.",
    swimPre: "Vor dem Start",
    preShort: "vorher",
    save: "Plan speichern",
    saveCta: "Speichern",
    saved: "Gespeichert",
    savedPlans: "Gespeicherte Pläne",
    planName: "Name des Plans",
    planNameHint: "z. B. neuseenMan 2026",
    raceDate: "Renndatum",
    newPlan: "Neuer Plan",
    load: "Laden",
    del: "Löschen",
    noPlans: "Noch keine Pläne gespeichert.",
    updated: "Aktualisiert",
    savedToast: "Plan gespeichert",
    updatedToast: "Plan aktualisiert",
    deletedToast: "Plan gelöscht",
    saveNote:
      "Der Plan bleibt auf diesem Gerät gespeichert und ist beim nächsten Öffnen wieder da.",
    myPlans: "Meine Pläne",
    unsaved: "Nicht gespeichert",
    totals: "Gesamt",
    totalCarbs: "KH gesamt",
    totalTime: "Gesamtzeit",
    avgPerHour: "Ø KH pro Stunde",
    perLeg: "Pro Disziplin",
    weight: "Dein Gewicht",
    weightField: "Körpergewicht",
    duration: "Race-Dauer",
    from: "von",
    to: "bis",
    minutes: "Min",
    bottle: "Flaschengröße",
    bottleField: "Volumen",
    cup: "Ø Trinkmenge pro Becher",
    cupField: "pro Becher",
    stations: "Verpflegungsstationen",
    stationsShort: "Stationen",
    next: "Weiter",
    back: "Zurück",
    calcCta: "Plan berechnen",
    editInputs: "Eingaben ändern",
    drinks: "Getränke",
    gels: "Gels",
    bars: "Riegel",
    none: "Kein",
    fluid: "Flüssigkeit",
    count: "Anzahl",
    addLine: "Weiteres Produkt",
    resultTitle: "Dein Plan",
    perHour: "KH-Aufnahme pro Stunde",
    ceiling: "Dein Limit nach Körpergewicht",
    ceilingShort: "Limit",
    ofCeiling: "deines Limits",
    onTarget: "Im Zielbereich",
    over: "über Limit",
    under: "unter Limit",
    total: "Gesamt-KH",
    kcal: "Kalorien",
    bottles: "Flaschen pro Rennen",
    cups: "Becher pro Rennen",
    perStation: "Becher pro Station",
    items: "Gels + Riegel",
    eatEvery: "Essen alle",
    drinkEvery: "Trinken (4 Schluck)/ Essen alle",
    schedule: "Zeitplan",
    sips: "4 Schluck trinken",
    gel: "Gel",
    bar: "Riegel",
    drink: "Trinken",
    disclaimer:
      "Kohlenhydrate werden nur mit ausreichender Flüssigkeitszufuhr optimal aufgenommen. An Verpflegungsstationen können zusätzlich Wasserflaschen gereicht werden.",
    emptyProducts: "Wähle mindestens ein Produkt.",
    liveNow: "Aktuell gewählt",
    liveOf: "von",
    liveCeiling: "Limit",
    liveNothing: "Noch nichts gewählt",
    swimNoLimit: "Kein Limit relevant",
    carbsSelected: "KH gewählt",
    swimDisclaimer:
      "Im Wasser wird nicht verpflegt — das Gel zählt zur Gesamtmenge, damit die Bilanz über das ganze Rennen stimmt.",
    rangeNote:
      "Schnellste und langsamste Zeit — daraus ergibt sich die Spanne. Angaben in Minuten.",
    durationOrder: "„bis“ muss größer als „von“ sein",
    nextToFuel: "Weiter zur Verpflegung",
    stepMeta: "Schritt 1 von 3",
    legsEyebrow: "Etappen",
    legsOne: "1 Etappe",
    legsMany: "Etappen",
    installTitle: "Carbdial auf den Startbildschirm",
    installBody: "Dann ist dein Plan am Renntag auch ohne Empfang da.",
    installCta: "Hinzufügen",
    installIos: "In Safari: Teilen-Symbol antippen, dann „Zum Home-Bildschirm“.",
    installDismiss: "Nicht jetzt",
  },
  en: {
    lang: "EN",
    appName: "carbdial",
    steps: ["Race", "Fuel", "Plan"],
    disciplineQ: "Which sport?",
    bike: "Bike",
    run: "Run",
    swim: "Swim",
    sportBike: "Bike race",
    sportRun: "Running (marathon, trail …)",
    sportTri: "Triathlon (swim · bike · run)",
    sportDu: "Duathlon (run · bike · run)",
    nextTo: "Next:",
    stageBike: "Bike leg",
    stageRun: "Run leg",
    stageSwim: "Swim leg",
    swimHint:
      "Nobody drinks while swimming. One gel about a minute before the start is normal — we count it so the totals add up.",
    swimPre: "Before the start",
    preShort: "pre",
    save: "Save plan",
    saveCta: "Save",
    saved: "Saved",
    savedPlans: "Saved plans",
    planName: "Plan name",
    planNameHint: "e.g. neuseenMan 2026",
    raceDate: "Race date",
    newPlan: "New plan",
    load: "Load",
    del: "Delete",
    noPlans: "No plans saved yet.",
    updated: "Updated",
    savedToast: "Plan saved",
    updatedToast: "Plan updated",
    deletedToast: "Plan deleted",
    saveNote: "The plan stays on this device and will be here next time you open the app.",
    myPlans: "My plans",
    unsaved: "Unsaved",
    totals: "Total",
    totalCarbs: "Total carbs",
    totalTime: "Total time",
    avgPerHour: "Avg carbs per hour",
    perLeg: "Per discipline",
    weight: "Your weight",
    weightField: "Body weight",
    duration: "Race duration",
    from: "from",
    to: "to",
    minutes: "min",
    bottle: "Bottle size",
    bottleField: "Volume",
    cup: "Avg. volume per cup",
    cupField: "per cup",
    stations: "Aid stations",
    stationsShort: "Stations",
    next: "Next",
    back: "Back",
    calcCta: "Calculate plan",
    editInputs: "Edit inputs",
    drinks: "Drinks",
    gels: "Gels",
    bars: "Bars",
    none: "None",
    fluid: "Fluid",
    count: "Count",
    addLine: "Another product",
    resultTitle: "Your plan",
    perHour: "Carb intake per hour",
    ceiling: "Your ceiling by body weight",
    ceilingShort: "Ceiling",
    ofCeiling: "of your ceiling",
    onTarget: "In range",
    over: "over ceiling",
    under: "under ceiling",
    total: "Total carbs",
    kcal: "Calories",
    bottles: "Bottles per race",
    cups: "Cups per race",
    perStation: "Cups per station",
    items: "Gels + bars",
    eatEvery: "Eat every",
    drinkEvery: "Drink (4 sips) / eat every",
    schedule: "Schedule",
    sips: "Drink 4 sips",
    gel: "Gel",
    bar: "Bar",
    drink: "Drink",
    disclaimer:
      "Carbohydrate is only absorbed properly alongside enough fluid. Aid stations may also hand out water bottles.",
    emptyProducts: "Pick at least one product.",
    liveNow: "Selected so far",
    liveOf: "of",
    liveCeiling: "ceiling",
    liveNothing: "Nothing selected yet",
    swimNoLimit: "No limit applies",
    carbsSelected: "carbs selected",
    swimDisclaimer:
      "No fuelling happens in the water — the gel counts toward the total so the whole-race balance adds up.",
    rangeNote: "Your fastest and slowest time — the range is what produces the g/h span.",
    durationOrder: "“to” must be greater than “from”",
    nextToFuel: "Next: Fuel",
    stepMeta: "Step 1 of 3",
    legsEyebrow: "Legs",
    legsOne: "1 leg",
    legsMany: "legs",
    installTitle: "Add Carbdial to your home screen",
    installBody: "Your plan is then there on race day, even with no reception.",
    installCta: "Add",
    installIos: "In Safari: tap the share icon, then “Add to Home Screen”.",
    installDismiss: "Not now",
  },
};
